import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";

import {
    BorderWidths,
    Colors,
    Radii,
    FontFamilies,
    FontWeights,
    FontSizes,
    LineHeights
} from "@paclife/plds.tokens";
import { Icon } from "@paclife/plds.icon";

const Checkmark = styled(Icon)`
    position: absolute;
    width: 16px;
    height: 16px;
    top: 17px;
    left: 17px;
    display: none; // to hide svg initially
`;

const Input = styled.input`
    /** Hide native checkbox */
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
`;

const Label = styled.label`
    background-color: #fff;
    border: ${BorderWidths.hairline} solid ${Colors.neutral300};
    border-radius: ${Radii.rounded};

    display: block;
    position: relative;
    padding: 12px 12px 12px 46px;
    cursor: pointer;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;

    ${({ width }) =>
        width &&
        css`
            width: ${width}px;
            min-width: fit-content;
        `};

    /* style text */
    font-family: ${FontFamilies.openSans};
    font-weight: ${FontWeights[400]};
    font-size: ${FontSizes[700]};
    line-height: ${LineHeights[200]};
    color: ${Colors.textBodyLight};
`;

// fake checkbox
const CustomBox = styled.span`
    position: absolute;
    top: 12px;
    left: 12px;
    height: 24px;
    width: 24px;
    border: ${BorderWidths.hairline} solid ${Colors.neutral500};
    border-radius: 2px;
    background-color: #fff;
`;

const Wrapper = styled.div`
    margin-top: 24px;

    /* unselected, hovered */
    Input:hover + Label {
        border-color: ${Colors.blue500};

        .customBox {
            border-color: ${Colors.blue500};
            box-shadow: 0px 0px 0px 6px #bbddfa;
        }
    }

    /* unselected, focused */
    Input:focus-visible + Label {
        border-color: ${Colors.blue700};

        .customBox {
            border-color: ${Colors.neutral900};
            box-shadow: 0px 0px 0px 6px #53a5ed;
        }
    }

    /* selected, default */
    Input:checked + Label {
        border-color: ${Colors.blue600};

        .customBox {
            background-color: ${Colors.primary};
            border-color: ${Colors.primary};
        }

        /* note that the Checkmark component is an svg */
        /* display the checkmark when input is checked */
        & > svg {
            display: block;
        }
    }

    /* selected, hovered */
    Input:checked:hover + Label {
        border-color: ${Colors.blue800};

        .customBox {
            background-color: ${Colors.primary};
            border-color: ${Colors.primary};
            box-shadow: 0px 0px 0px 6px #bbddfa;
        }
    }

    /* selected, focused */
    Input:checked:focus-visible + Label {
        border-color: ${Colors.blue1000};

        .customBox {
            background-color: ${Colors.primary};
            border-color: ${Colors.primary};
            box-shadow: 0px 0px 0px 6px #53a5ed;
        }
    }

    ${({ dark }) =>
        dark &&
        css`
            /* unselected, dark mode */
            Label {
                border-color: ${Colors.blue600};
            }
            /* unselected, hovered, dark mode */
            Input:hover + Label {
                border-color: ${Colors.blue200};

                .customBox {
                    border-color: ${Colors.neutral700};
                }
            }
            /* unselected, focused, dark mode */
            Input:focus-visible + Label {
                border-color: ${Colors.blue300};

                .customBox {
                    border-color: ${Colors.neutral900};
                }
            }
            /* selected, dark mode */
            Input:checked + Label {
                border-color: ${Colors.blue200};
            }
            /* selected, focused, dark mode */
            Input:checked:focus-visible + Label {
                border-color: ${Colors.blue300};
            }
        `}


        ${({ nowrap }) =>
        nowrap &&
        css`
            /* unselected, nowrap mode */
            Label {
                border: none;
            }
            /* unselected, hovered, nowrap mode */
            Input:hover + Label {
                border: none;

                .customBox {
                    border: none;
                }
            }
            /* unselected, focused, nowrap mode */
            Input:focus-visible + Label {
                border: none;

                .customBox {
                    border: none;
                }
            }
            /* selected, dark mode */
            Input:checked + Label {
                border: none;
            }
            /* selected, focused, dark mode */
            Input:checked:focus-visible + Label {
                border: none;
            }
        `}

    /* disabled, unchecked */
  Input:disabled + Label {
        background-color: ${Colors.neutral100};
        border-color: ${Colors.neutral300};

        /* label text color */
        color: ${Colors.disabled};

        .customBox {
            background-color: ${Colors.neutral100};
            border-color: ${Colors.neutral300};
            box-shadow: none;
        }
    }

    /* disabled, checked */
    Input:checked:disabled + Label {
        background-color: ${Colors.neutral100};
        border-color: ${Colors.neutral300};

        /* label text color */
        color: ${Colors.disabled};

        .customBox {
            background-color: ${Colors.neutral300};
            border-color: ${Colors.neutral300};
            box-shadow: none;
        }
    }
`;

function Checkbox({
    checked: checkedProp,
    dark = false,
    disabled = false,
    nowrap = false,
    id,
    label,
    name,
    onChange,
    value,
    width
}) {
    const [checked, setChecked] = useState(checkedProp);
    useEffect(() => {
        setChecked(checkedProp);
    }, [checkedProp]);
    return (
        <Wrapper dark={dark} nowrap={ nowrap }>
            <Input
                type="checkbox"
                id={id}
                name={name}
                value={value}
                checked={checked}
                disabled={disabled}
                onChange={onChange}
            />
            <Label htmlFor={id} width={width}>
                {label}
                <CustomBox className="customBox" />
                <Checkmark name="checkmark" color="#fff" />
            </Label>
        </Wrapper>
    );
}

export default Checkbox;
